import React from 'react';
import { CONTENT_ARCHIVE } from 'config/types/prop';
import setInnerHTML from 'utils/setInnerHTML';
import ContentList from 'components/contentList';
import withStyles from 'isomorphic-style-loader/withStyles';
import ThemeProvider from 'components/hoc/themeProvider';
import Meta from 'components/meta';
import ShowMoreButton from 'components/showMoreButton';
import HeaderLinks from 'components/headerLinks';
import useAccessibilityFocusLastAfterLoading from 'hooks/useAccessibilityFocusLastAfterLoading';
import { buildCanonicalLink } from 'utils/url';
import getNavigationText from 'selectors/getNavigationText';
import { getLanguage } from 'selectors/getMatch';
import getIsInProductMagazine from 'selectors/getIsInProductMagazine';
import { connect } from 'react-redux';

import styles from './contentArchiveMultiple.css';

// construct urls from list of links and check for a path
// helper for getRobotsNoindex
function checkListForPath(linkList, archiveType, langCode, path) {
  return linkList
    .map((exploreLink) => `/${langCode}/magazine/${archiveType}${exploreLink.url}`)
    .includes(path);
}

function getRobotsNoindex(navigationTextObj, language, pathname) {
  // decide whether to index this page
  const shouldIndex = (
    checkListForPath(navigationTextObj.aboutLanguages, 'tags', language, pathname) ||
    checkListForPath(navigationTextObj.categories, 'category', language, pathname)
  );

  return shouldIndex ? [] : [{ name: 'robots', content: 'noindex' }];
}

const ContentArchiveMultiple = ({
  items,
  loadingMore,
  onLoadMore,
  pagination: { hasMore },
  term: { name },
  location: { pathname },
  currentEdition,
  navigationTextObj,
  language,
  isInProductMagazine,
}) => {
  const onFocus = useAccessibilityFocusLastAfterLoading({ loading: loadingMore });
  // tells search engine to index/not index page as desired
  const robotsNoindex = getRobotsNoindex(navigationTextObj, language, pathname);

  const showLoadMore = hasMore || loadingMore;
  const canonicalLink = buildCanonicalLink(currentEdition, pathname);

  // tells view where to slice items so that the view below the two featured articles always has complete rows of three
  const lastItemIndex = (hasMore ?
    items.length - ((items.length - 2) % 3) :
    items.length);

  return (
    <section className={styles.wrapper}>
      <Meta
        metaTags={robotsNoindex}
        title={`${name} - Babbel Magazine`}
      />
      <HeaderLinks
        canonicalUrl={canonicalLink}
      />
      <div className={styles.content}>
        {!isInProductMagazine && <h1
          className={styles.heading}
          dangerouslySetInnerHTML={setInnerHTML(name)} // eslint-disable-line react/no-danger
        />}
        <div className={styles.archiveList}>
          <div className={styles.archiveFeatures}>
            <ThemeProvider themes={{
              DefaultListViewItem: 'twoUp',
              ContentList: 'twoUp',
            }}
            >
              <ContentList items={items.slice(0, 2)} onFocus={onFocus} />
            </ThemeProvider>
          </div>
          <ThemeProvider themes={{
            DefaultListViewItem: 'threeUp',
            ContentList: 'threeUp',
          }}
          >
            <ContentList
              items={
                items.slice(2, lastItemIndex)
              }
              interstitials={[
                {
                  frequency: 3,
                  count: Infinity,
                  content: <div className={styles.divider} />,
                },
              ]}
              onFocus={onFocus}
            />
          </ThemeProvider>
        </div>
        {showLoadMore &&
          <ShowMoreButton
            onClick={onLoadMore}
            disabled={loadingMore}
            loading={loadingMore}
          />
        }
      </div>
    </section>
  );
};

ContentArchiveMultiple.propTypes = CONTENT_ARCHIVE;

const wrapWithStyles = withStyles(styles);

const mapStateToProps = (state) => ({
  navigationTextObj: getNavigationText(state),
  language: getLanguage(state),
  isInProductMagazine: getIsInProductMagazine(state),
});

const withRedux = connect(mapStateToProps);

export default withRedux(wrapWithStyles(ContentArchiveMultiple));
